'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '../../lib/utils';

import {
  HoverSurfaceInteractionContext,
  useHoverSurfaceClosePolicy,
  useHoverSurfaceTriggerHandlers,
} from './hover-surface-close-policy';

const TooltipProvider = TooltipPrimitive.Provider;

/**
 * Holds the tooltip open through a click on its own trigger (HAR-4016):
 * clicking the ⓘ must not take away the text describing it. The policy — and
 * the reasoning behind its narrow scope — lives in
 * `hover-surface-close-policy.tsx`, shared with `HoverCard`.
 */
const Tooltip = ({
  open,
  defaultOpen,
  onOpenChange,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>) => {
  const policy = useHoverSurfaceClosePolicy({
    open,
    defaultOpen,
    onOpenChange,
  });

  return (
    <HoverSurfaceInteractionContext.Provider value={policy.interaction}>
      <TooltipPrimitive.Root
        {...props}
        open={policy.open}
        onOpenChange={policy.onOpenChange}
      >
        {children}
      </TooltipPrimitive.Root>
    </HoverSurfaceInteractionContext.Provider>
  );
};
// A literal, not Radix's name: this is a wrapper with its own state, and it
// should be visible as such in React DevTools and error traces.
Tooltip.displayName = 'Tooltip';

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onPointerDown, onClick, ...props }, ref) => {
  const handlers = useHoverSurfaceTriggerHandlers({ onPointerDown, onClick });

  // Handlers after the spread so a caller's props cannot replace them.
  return <TooltipPrimitive.Trigger ref={ref} {...props} {...handlers} />;
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

// A tooltip describes whatever it is anchored to, so it has to stack above every
// surface that can contain a trigger — including a Popover, whose content sets
// 99999. Radix copies the content's *computed* z-index onto the portalled popper
// wrapper, and that wrapper is what actually stacks, so the value must be set
// through `style` (a `z-*` class leaves the wrapper at `auto` and the tooltip
// renders behind the popover).
const TOOLTIP_Z_INDEX = 100000;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, style, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      // `style` is pulled out of `props` and merged: passing it through the
      // spread would replace this object wholesale and drop the z-index. A
      // caller can still override zIndex deliberately — its style spreads last.
      style={{ zIndex: TOOLTIP_Z_INDEX, ...style }}
      className={cn(
        'overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-tooltip-content-transform-origin]',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * The little pointer that ties a tooltip to its trigger.
 *
 * Available, but nothing renders one yet — a tooltip that wants to be visually
 * tethered opts in. Four things bite the first time you use it:
 *
 * 1. **`TooltipContent` sets `overflow-hidden`.** The arrow is positioned
 *    OUTSIDE the content box, so it is clipped away entirely unless the same
 *    content also gets `overflow-visible`. The default stays `hidden` because
 *    ~218 existing tooltips rely on it to clip their rounded corners.
 * 2. **It must be a DIRECT child of `TooltipContent`.** Radix positions it
 *    against the popper wrapper, so wrapping it in a div of your own shifts it
 *    off the edge. (Any depth works for the React context; only layout breaks.)
 * 3. **Raise `sideOffset`.** It defaults to 4, and Radix folds the arrow's own
 *    height into the offset, so an arrow at the default sits on the trigger.
 * 4. **Match the fill to the surface.** This defaults to `bg-popover`, which
 *    `TooltipContent` uses. `BaseTooltip.Content` overrides the surface to
 *    `bg-card`, so a tooltip built on that must pass `bg-card fill-card`.
 *
 * ```tsx
 * <TooltipContent sideOffset={8} className="overflow-visible">
 *   {children}
 *   <TooltipArrow />
 * </TooltipContent>
 * ```
 *
 * `apps/heartplus/.../basic-info/phone-note-tooltip.tsx` hand-rolls a rotated
 * square for the same effect and strips the content's styling to show it; this
 * is its eventual replacement.
 */
const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    // `bg-*` paints the rotated square, `fill-*` covers the SVG Radix renders
    // underneath it; both are needed or the arrow shows as a dark wedge.
    className={cn(
      'size-2.5 rotate-45 rounded-[0.125rem] bg-popover fill-popover',
      className,
    )}
    {...props}
  />
));
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipArrow,
};
